/**
 * RunOutput — the output of a step or a run in the execution log.
 *
 * Free text, JSON and arrays stay on the host that ran the mission
 * (ADR-20261009 Part 6.1, agion/Agion#2911). The server sends a description in
 * their place, and a reference when the host keeps the value. This component
 * decides nothing about that: a description with the server's reference shows
 * an Open action that asks the server to open the value on its host; one
 * without a reference cannot be opened. The split and the open are shared with
 * the next twin (keptOutput.ts).
 */

import { Text } from '../../atoms/Typography';
import { Button } from '../../atoms/Button';
import { ContentBlock } from './ContentBlock';
import {
  hasRunOutputValues,
  keptOutputLabel,
  keptOutputMessage,
  splitRunOutput,
  useKeptOutputOpen,
  type DescribedOutput,
  type KeptOutputLabelKey,
  type OpenKeptOutput,
} from './keptOutput';

export { splitRunOutput } from './keptOutput';
export type { KeptOutputLabelKey, KeptOutputOpenRequest, OpenKeptOutput } from './keptOutput';

interface KeptOutputProps {
  output: DescribedOutput;
  missionId?: string | undefined;
  labels: Partial<Record<KeptOutputLabelKey, string>>;
  onOpen?: OpenKeptOutput | undefined;
}

function KeptOutput({ output, missionId, labels, onOpen }: KeptOutputProps) {
  const { state, canOpen, open, openedRef } = useKeptOutputOpen(output, missionId, onOpen);

  if (state.phase === 'opened') {
    return (
      <div ref={openedRef} tabIndex={-1} role="region" aria-label={output.name} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded-md">
        <ContentBlock content={state.content} isJson={output.isJson} variant="default" label={output.name} />
      </div>
    );
  }
  return (
    <div className="rounded-md bg-slate-50 dark:bg-slate-800 px-2.5 py-2 space-y-1.5" data-testid="kept-output">
      <Text as="div" size="xs" className="font-mono truncate">{output.name}</Text>
      <div className="flex items-center justify-between gap-2">
        <Text as="p" size="xs" color="muted" aria-live="polite">
          {keptOutputMessage(state, labels)}
        </Text>
        {canOpen && (
          <Button
            variant="outline"
            size="xs"
            isLoading={state.phase === 'opening'}
            aria-label={keptOutputLabel(labels, 'openKeptOutputNamed', output.name)}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              open();
            }}
          >
            {keptOutputLabel(labels, 'openKeptOutput')}
          </Button>
        )}
      </div>
    </div>
  );
}

export interface RunOutputProps {
  output: unknown;
  /** The mission whose run produced the output; a kept output opens with it. */
  missionId?: string | undefined;
  label: string;
  labels?: Partial<Record<KeptOutputLabelKey, string>>;
  onOpen?: OpenKeptOutput | undefined;
}

export function RunOutput({ output, missionId, label, labels = {}, onOpen }: RunOutputProps) {
  const split = splitRunOutput(output);
  const { values, described } = split;
  return (
    <>
      {hasRunOutputValues(split) && (
        <ContentBlock
          content={values as string | Record<string, unknown>}
          isJson={typeof values !== 'string'}
          variant="default"
          label={label}
        />
      )}
      {described.map((item) => (
        <KeptOutput key={item.name} output={item} missionId={missionId} labels={labels} onOpen={onOpen} />
      ))}
    </>
  );
}
